import React, { useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { Checkbox } from '@pkg/components';
import { type Collectible } from '@pkg/graphql/codegen/graphql';
import { useBreakpoints } from '@pkg/hooks';

import { AssetInfo } from '@app/pages/ProfilePage/components/ListView/AssetInfo';
import { getPricingData, PRICE_TYPES } from '@app/pages/ProfilePage/utils';

import { ListingButton } from './ListingButton';
import { PriceField } from './PriceField';

import {
    Cell,
    DataLabel,
    DataRow,
    ExpandedData,
    ExpandedInside,
    InfoCell,
    MobileClickCover,
    Row,
    ToggleArea,
} from './styles';

interface Props {
    item: Collectible;
    checked: boolean;
    setChecked: () => void;
}

export const ListEntry = ({ item, checked, setChecked }: Props) => {
    const { isMobile } = useBreakpoints();
    const [isExpanded, setIsExpanded] = useState(false);

    const [_chain, _contract, _token_id] = item?.id?.split('.') || [];
    const _image = item.imageUrl;
    const _name = item.name;
    const _collection_name = '';
    const _collection_slug = item.collection?.slug || '';
    const _asset_url = `/collectible/${_chain}/${_contract}/${_token_id}?goBack=1`;

    const listingData = item?.orders?.find((order) => !!order.price);

    const infoCellMarkup = (
        <InfoCell>
            <Checkbox checked={checked} onChange={setChecked} />
            <AssetInfo
                image={_image}
                name={_name}
                collection_slug={_collection_slug}
                collection_name={_collection_name}
                asset_url={_asset_url}
            />
            {isMobile && <MobileClickCover />}
        </InfoCell>
    );

    const priceData = useMemo(
        () =>
            getPricingData({
                floorPriceWei: item.collection?.marketData?.floorPrice || 0,
                comparisonPrices: {
                    listPrice: { value: listingData?.price || 0 },
                    offerPrice: { value: listingData?.price || 0 }, // TODO: add right price when BE returns it
                    lastSalePrice: { value: listingData?.price || 0 }, // TODO: add right price when BE returns it
                },
            }),
        [item.collection?.marketData?.floorPrice, listingData?.price],
    );
    const floorPriceCellMarkup = (
        <Cell>
            <PriceField value={priceData.usdFloorPrice} currency={'usd'} />
        </Cell>
    );

    const priceCellMarkup = PRICE_TYPES.map((priceType) => {
        const priceUsd = priceData?.comparisonValues?.[priceType]?.usdValue;
        const priceDiff = priceData?.comparisonValues?.[priceType]?.valueDiff;
        const diffDirection = priceDiff && priceDiff > 0 ? 'up' : 'down';
        const labels = {
            lastSalePrice: 'Last sale',
            listPrice: 'List Price',
            offerPrice: 'Offer Price',
        };
        if (isMobile) {
            return (
                <DataRow key={priceType}>
                    <DataLabel>{labels[priceType]}</DataLabel>
                    <Cell>
                        {priceUsd ? (
                            <PriceField
                                value={priceUsd}
                                currency={'usd'}
                                percent={Math.abs(priceDiff || 0)}
                                diffDirection={diffDirection}
                            />
                        ) : (
                            '-'
                        )}
                    </Cell>
                </DataRow>
            );
        }
        return (
            <Cell key={priceType}>
                {priceUsd ? (
                    <PriceField
                        value={priceUsd}
                        currency={'usd'}
                        percent={Math.abs(priceDiff || 0)}
                        diffDirection={diffDirection}
                    />
                ) : (
                    '-'
                )}
            </Cell>
        );
    });

    const buttonCellMarkup = (
        <Cell>
            <ListingButton isListed={!!listingData} item={item} />
        </Cell>
    );

    if (isMobile) {
        return (
            <Row>
                <ToggleArea onClick={() => setIsExpanded(!isExpanded)}>
                    {infoCellMarkup}
                    {buttonCellMarkup}
                </ToggleArea>
                <AnimatePresence>
                    {isExpanded && (
                        <ExpandedData
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <ExpandedInside>
                                <DataRow>
                                    <DataLabel>Floor</DataLabel>
                                    {floorPriceCellMarkup}
                                </DataRow>
                                {priceCellMarkup}
                            </ExpandedInside>
                        </ExpandedData>
                    )}
                </AnimatePresence>
            </Row>
        );
    }

    return (
        <Row>
            {infoCellMarkup}
            {floorPriceCellMarkup}
            {priceCellMarkup}
            {buttonCellMarkup}
        </Row>
    );
};
